import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { useBasicPopover } from '@kc-console/common-ui';

import type { PropsWithChildren } from 'react';

export interface SearchPopoverContext {
  closePopover: () => void;
  focusedIndex: number;
  inputValue: string;
  popoverProps: ReturnType<typeof useBasicPopover>;
  preserveOnClick: (e: React.MouseEvent) => void;
  setFocusedIndex: (index: number) => void;
  setInputValue: (value: string) => void;
  setTotalItems: (count: number) => void;
  totalItems: number;
}

export const useSearchPopover = () => {
  const context = useContext(SearchPopoverContext);
  if (!context) {
    throw new Error('useSearchPopover must be used within SearchPopoverProvider');
  }
  return context;
};

export const SearchPopoverContext = createContext<SearchPopoverContext | null>(null);

export const SearchPopoverProvider = ({ children }: PropsWithChildren) => {
  const popoverProps = useBasicPopover();
  const [inputValue, setInputValue] = useState('');
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [totalItems, setTotalItems] = useState(0);

  useEffect(() => {
    if (popoverProps.open) {
      setInputValue('');
      setFocusedIndex(-1);
    }
  }, [popoverProps.open]);

  useEffect(() => {
    setFocusedIndex(-1);
  }, [inputValue]);

  const preserveOnClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  const closePopover = useCallback(() => {
    popoverProps.onClose();
  }, [popoverProps]);

  const value = useMemo(
    () => ({
      popoverProps,
      inputValue,
      setInputValue,
      preserveOnClick,
      closePopover,
      focusedIndex,
      setFocusedIndex,
      totalItems,
      setTotalItems,
    }),
    [popoverProps, inputValue, preserveOnClick, closePopover, focusedIndex, totalItems],
  );

  return <SearchPopoverContext.Provider value={value}>{children}</SearchPopoverContext.Provider>;
};
